<template>
  <div class="flex h-full flex-col">
    <header class="flex items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
      <h3 class="text-lg font-semibold text-gray-900">{{ title }}</h3>
      <div class="flex flex-wrap items-center gap-2">
        <button
          type="button"
          class="rounded-lg px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900"
          @click="$emit('close')"
        >
          Retour
        </button>
      </div>
    </header>

    <div
      ref="pdfScrollContainer"
      class="min-h-0 h-full flex-1 overflow-y-auto overscroll-contain bg-gray-100 px-2 py-2 sm:px-4"
      @scroll.passive="handlePdfScroll"
    >
      <div v-if="pdfLoading" class="flex items-center justify-center h-[65vh] text-sm text-gray-600">
        Chargement du document...
      </div>
      <div v-else-if="pdfLoadError" class="flex flex-col items-center justify-center gap-2 h-[65vh] text-sm">
        <p class="text-red-600">{{ pdfLoadError }}</p>
        <a
          :href="documentUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="text-blue-600 underline hover:text-blue-800"
        >
          Ouvrir le document dans un nouvel onglet
        </a>
      </div>
      <div v-else class="mx-auto flex max-w-4xl flex-col gap-3 pb-4">
        <img
          v-for="page in renderedPages"
          :key="page.pageNumber"
          :src="page.src"
          :alt="`Page ${page.pageNumber}`"
          class="w-full rounded-sm bg-white shadow-xs"
        />
      </div>
    </div>

    <footer class="border-t border-gray-200 px-5 py-4">
      <div :class="canConfirmViewed ? '' : 'opacity-70'">
        <Checkbox
          :id="`${idPrefix}_viewed_confirmation`"
          :name="`${idPrefix}_viewed_confirmation`"
          :label="checkboxLabel"
          labelClass="font-normal text-gray-800"
          :disabled="!canConfirmViewed"
          v-model="checkedModel"
        />
      </div>
      <p v-if="!canConfirmViewed" class="mt-2 text-xs">
        Case désactivée : faites défiler le document jusqu'à la fin pour l'activer.
      </p>
      <p v-else class="mt-2 text-xs text-gray-500">Case activée : vous pouvez valider ce document.</p>
    </footer>
  </div>
</template>

<script>
import { Checkbox } from "@estaly/ui";
import axios from "axios";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";

const pdfWorker = new URL("pdfjs-dist/legacy/build/pdf.worker.min.mjs", import.meta.url).toString();

export default {
  name: "DocumentViewer",
  components: {
    Checkbox,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    documentUrl: {
      type: String,
      default: "",
    },
    checkboxLabel: {
      type: String,
      required: true,
    },
    checked: {
      type: Boolean,
      default: false,
    },
    idPrefix: {
      type: String,
      required: true,
    },
  },
  emits: ["close", "update:checked"],
  data() {
    return {
      renderedPages: [],
      pdfLoading: false,
      pdfLoadError: "",
      hasScrolledToDocumentEnd: false,
      userHasScrolled: false,
    };
  },
  computed: {
    canConfirmViewed() {
      return (
        this.userHasScrolled &&
        this.hasScrolledToDocumentEnd &&
        !this.pdfLoading &&
        !this.pdfLoadError &&
        this.renderedPages.length > 0
      );
    },
    checkedModel: {
      get() {
        return this.checked;
      },
      set(value) {
        if (value && !this.canConfirmViewed) return;
        this.$emit("update:checked", value);
        if (value) this.$emit("close");
      },
    },
  },
  watch: {
    documentUrl: {
      immediate: true,
      handler() {
        this.loadPdfDocument();
      },
    },
  },
  methods: {
    async loadPdfDocument() {
      this.renderedPages = [];
      this.pdfLoading = true;
      this.pdfLoadError = "";
      this.hasScrolledToDocumentEnd = false;
      this.userHasScrolled = false;

      if (!this.documentUrl) {
        this.pdfLoadError = "Le document n'est pas disponible.";
        this.pdfLoading = false;
        return;
      }

      try {
        pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;
        const response = await axios.get(this.documentUrl, { responseType: "arraybuffer", timeout: 30000 });
        const pdfData = new Uint8Array(response.data);
        const loadingTask = pdfjsLib.getDocument({ data: pdfData });
        const pdf = await loadingTask.promise;
        const pages = [];
        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
          const page = await pdf.getPage(pageNumber);
          const viewport = page.getViewport({ scale: 1.2 });
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await page.render({ canvasContext: context, viewport }).promise;
          pages.push({ pageNumber, src: canvas.toDataURL("image/jpeg", 0.92) });
        }
        this.renderedPages = pages;
        this.$nextTick(() => this.handlePdfScroll());
      } catch (error) {
        this.pdfLoadError = "Impossible de charger le document dans la visionneuse.";
      } finally {
        this.pdfLoading = false;
      }
    },
    handlePdfScroll() {
      const container = this.$refs.pdfScrollContainer;
      if (!container) return;
      this.userHasScrolled = true;
      const threshold = 24;
      this.hasScrolledToDocumentEnd =
        container.scrollTop + container.clientHeight >= container.scrollHeight - threshold;
    },
  },
};
</script>
